/**
 * CR401: Templates List Tab
 * 
 * Displays a list/grid of templates by deal_type_key × variant_key.
 * Primary CTA: Edit Template (opens standalone Template Editor route)
 */

import { useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { formatDistanceToNow } from "date-fns";
import { Edit, FileText, CheckCircle2, AlertCircle } from "lucide-react";
import type { DealTypeRegistryEntry, DealCardTemplate } from "@/providers/DealCardConfigProvider";

interface TemplatesListTabProps {
  templates: DealCardTemplate[];
  registry: DealTypeRegistryEntry[];
  loading: boolean;
  onEditTemplate: (dealTypeKey: string, variantKey: string) => void;
  onRefresh: () => void;
}

interface TemplateRow {
  dealTypeKey: string;
  variantKey: string;
  label: string;
  hasTemplate: boolean;
  updatedAt: string | null;
  isEnabled: boolean;
}

export function TemplatesListTab({
  templates,
  registry,
  loading,
  onEditTemplate,
}: TemplatesListTabProps) {
  
  // Build template rows - one row per deal type with template status
  const templateRows: TemplateRow[] = useMemo(() => {
    const templateMap = new Map(templates.map(t => [t.deal_type_key, t]));
    
    return registry.map(entry => {
      const template = templateMap.get(entry.deal_type_key);
      return {
        dealTypeKey: entry.deal_type_key,
        variantKey: "standard_v2", // CR401: Currently only supporting standard_v2
        label: entry.label,
        hasTemplate: !!template,
        updatedAt: template?.updated_at || null,
        isEnabled: entry.is_enabled,
      };
    }).sort((a, b) => a.dealTypeKey.localeCompare(b.dealTypeKey));
  }, [templates, registry]);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-96" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Card Templates
        </CardTitle>
        <CardDescription>
          Templates control the visual composition of deal cards for each deal type.
          Click Edit to open the Template Editor.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ScrollArea className="max-h-[600px]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[140px]">Deal Type</TableHead>
                <TableHead>Variant</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead className="text-right">Last Updated</TableHead>
                <TableHead className="w-[100px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {templateRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                    No templates found. Create one by clicking Edit.
                  </TableCell>
                </TableRow>
              ) : (
                templateRows.map((row) => (
                  <TableRow key={`${row.dealTypeKey}-${row.variantKey}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="font-mono text-xs">
                          {row.dealTypeKey}
                        </Badge>
                        {!row.isEnabled && (
                          <Badge variant="secondary" className="text-[10px]">
                            Disabled
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">{row.variantKey}</span>
                    </TableCell>
                    <TableCell className="text-center">
                      {row.hasTemplate ? (
                        <Badge variant="default" className="gap-1">
                          <CheckCircle2 className="h-3 w-3" />
                          Configured
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="gap-1">
                          <AlertCircle className="h-3 w-3" />
                          Default
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-sm text-muted-foreground">
                      {row.updatedAt ? (
                        formatDistanceToNow(new Date(row.updatedAt), { addSuffix: true })
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onEditTemplate(row.dealTypeKey, row.variantKey)}
                        className="gap-1"
                      >
                        <Edit className="h-3 w-3" />
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
